import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MdVerified } from "react-icons/md";
import { FaShareFromSquare } from "react-icons/fa6";
import NavbarTwo from "../../Components/Navbar/navbarTwo";
import Footer from "../../Components/Footer";
import BottomNavbar from "../../Components/Navbar/bottomNavbar";

interface Campaign {
  id: number;
  title: string;
  slug: string;
  image: string;
  target_donation: string;
  total_donations: string;
  category?: {
    id: number;
    name: string;
  };
  user?: {
    name: string;
  };
}

const categories = [
  { id: 1, name: "Keagamaan" },
  { id: 2, name: "Bencana Alam" },
  { id: 3, name: "Kemanusiaan" },
  { id: 4, name: "Pendidikan" },
  { id: 5, name: "Kesehatan" },
  { id: 6, name: "Kegiatan Sosial" },
  { id: 7, name: "Wakaf" },
  { id: 8, name: "Zakat" },
];

const truncateText = (text: string, max: number) => {
  if (text.length <= max) return text;
  return text.slice(0, max) + "...";
};

const DonasiUmumPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTitle, setExpandedTitle] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalProgram, setTotalProgram] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | "">("");

  // ✅ TAMBAHAN BARU
  const [searchParams, setSearchParams] = useSearchParams();

  const IconVerif = MdVerified as React.FC<React.SVGProps<SVGSVGElement>>;
  const IconShare = FaShareFromSquare as React.FC<React.SVGProps<SVGSVGElement>>;

  const handleShare = (item: Campaign) => {
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: `Ayo bantu campaign ini: ${item.title}`,
        url: `${window.location.origin}/donasi/${item.slug}`,
      });
    }
  };

  // ✅ Ambil category dari URL saat pertama load
  useEffect(() => {
    const categoryFromUrl = searchParams.get("category");

    if (categoryFromUrl) {
      setSelectedCategory(Number(categoryFromUrl));
      setPage(1);
    } else {
      setSelectedCategory("");
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);

      try {
        let url = `https://base.patunganindonesia.com/api/campaign?page=${page}`;

        if (search) url += `&search=${search}`;

        const categoryFromUrl = searchParams.get("category");
        if (categoryFromUrl) {
          url += `&category=${categoryFromUrl}`;
        }

        const res = await fetch(url);
        const json = await res.json();

        setCampaigns(json.data || []);
        setLastPage(json.meta.last_page || 1);
        setTotalProgram(json.meta.total || 0);
      } catch (error) {
        console.error("Gagal mengambil campaign:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [page, search, searchParams]);
  return (
    <>
      <NavbarTwo />

      <section className="bg-gray-100 py-24 md:py-28 font-signika">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <div className="mb-6 pt-0 md:pt-10 w-full">
            <div className="flex justify-between mb-2 items-center">
              <h1 className="text-xl md:text-2xl font-semibold">
                Eksplorasi <span className="text-primary2">Program</span>
              </h1>

              <div className="rounded-full bg-primary2 text-white px-4 py-1 md:py-2 text-sm">{totalProgram} Program</div>
            </div>

            <div className="flex gap-4 items-center justify-center">
              <div className="w-full">
                <input
                  type="text"
                  placeholder="Cari campaign..."
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  className="px-4 py-2 rounded-xl border w-full"
                />
              </div>

              <div>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : "";

                    setSelectedCategory(value);
                    setPage(1);

                    // ✅ UPDATE URL TANPA REFRESH
                    if (value) {
                      setSearchParams({ category: String(value) });
                    } else {
                      setSearchParams({});
                    }
                  }}
                  className="px-4 py-2 rounded-xl border bg-white md:w-64">
                  <option value="">Semua Kategori</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {/* LOADING */}
          {loading && (
            <div className="flex justify-center items-center py-20 gap-2">
              <span className="w-3 h-3 bg-primary2 rounded-full animate-bounce" />
              <span className="w-3 h-3 bg-primary2 rounded-full animate-bounce" />
              <span className="w-3 h-3 bg-primary2 rounded-full animate-bounce" />
            </div>
          )}

          {/* GRID */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 ">
            {campaigns.map((item) => {
              const collected = Number(item.total_donations);
              const target = Number(item.target_donation);
              const percentage = target ? Math.min(Math.round((collected / target) * 100), 100) : 0;

              const isExpanded = expandedTitle === item.id;

              return (
                <div key={item.id} className="relative bg-white shadow-md md:shadow-xl rounded-xl md:rounded-2xl overflow-hidden border flex flex-col">
                  <div className="absolute top-0 left-0 w-full h-1.5 md:h-2 bg-gradient-to-r from-[#29abe2] via-[#1f8ac0] to-[#8cc63f] " />

                  <Link to={`/donasi/${item.slug}`}>
                    <img src={`${item.image}`} alt={item.title} className="w-full h-[130px] md:h-[190px] object-cover" />
                  </Link>

                  <div className="p-3 md:p-5 text-gray-800 flex flex-col flex-grow">
                    <Link to={`/donasi/${item.slug}`}>
                      <h3 onClick={() => setExpandedTitle(isExpanded ? null : item.id)} className="text-xs md:text-lg font-bold mb-1 md:mb-2 cursor-pointer hover:text-primary2 transition leading-snug">
                        {isExpanded ? item.title : truncateText(item.title, 20)}
                      </h3>
                    </Link>

                    <div className="text-gray-500 text-[10px] md:text-sm italic mb-2 md:mb-3 flex items-center gap-1 md:gap-2">
                      {item.user?.name ?? "-"}
                      <span className="text-primary2">
                        <IconVerif />
                      </span>
                    </div>

                    <div className="flex justify-between text-[10px] md:text-sm mb-1">
                      <span className="text-primary2 font-semibold">Rp {collected.toLocaleString("id-ID")}</span>
                      <span>{percentage}%</span>
                    </div>

                    <div className="w-full h-1.5 md:h-2 bg-gray-300 rounded-full mb-2 md:mb-3">
                      <div className="h-full bg-primary2 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                    </div>

                    <div className="flex justify-between items-center mb-3 md:mb-4">
                      <p className="text-[10px] md:text-sm text-gray-500 italic">
                        Target:
                        <span className="text-gray-600 font-semibold ml-1">Rp {target.toLocaleString("id-ID")}</span>
                      </p>

                      <button onClick={() => handleShare(item)} className="hover:text-primary2 text-primary2 transition">
                        <IconShare />
                      </button>
                    </div>

                    {/* FIX BUTTON SUPAYA TIDAK HILANG */}
                    <Link to={`/donasi/${item.slug}`} className="mt-auto w-full text-center px-3 md:px-11 py-2 text-[11px] md:text-sm font-semibold text-white rounded-lg bg-primary2 hover:opacity-90 transition-all duration-300">
                      Patungan Sekarang
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* PAGINATION */}
          <div className="flex justify-center mt-10 gap-2 flex-wrap">
            {Array.from({ length: lastPage }, (_, i) => i + 1).map((num) => (
              <button key={num} onClick={() => setPage(num)} className={`w-10 h-10 rounded-full text-sm font-semibold transition ${page === num ? "bg-primary2 text-white shadow-lg" : "bg-white border hover:bg-gray-100"}`}>
                {num}
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <BottomNavbar />
    </>
  );
};

export default DonasiUmumPage;
